import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@parentsquare/squarekit/lib/utils';

/**
 * Component version. `v1` (default) paints the full-page surface with the
 * semantic `--color-surface-page` token (light/dark aware) and tokenized
 * all-sides padding. `v0` keeps the legacy hardcoded `#E0E0E0` surface.
 * Pass `version="v0"` to preserve the legacy render.
 */
export type PageVersion = 'v0' | 'v1';

/**
 * `surface` (default) paints the page background, padding, and full-page height.
 * `surfaceless` renders nothing — no background, padding, or min-height — for
 * pages that supply their own layout.
 */
export type PageVariant = 'surface' | 'surfaceless';

// Surface styling lives in version-gated compound variants so `surfaceless`
// resolves to no classes regardless of version. Both axes have defaults, so a
// caller that omits them still resolves the styled `surface` + `v1` compound.
const pageVariants = cva('', {
  variants: {
    version: {
      v0: '',
      v1: '',
    },
    variant: {
      surface: '',
      surfaceless: '',
    },
  },
  compoundVariants: [
    {
      variant: 'surface',
      version: 'v0',
      className: 'tw:bg-[#E0E0E0] tw:h-max tw:min-h-screen tw:pl-3 tw:pr-3',
    },
    {
      variant: 'surface',
      version: 'v1',
      className: 'tw:h-max tw:min-h-screen tw:bg-(--color-surface-page) tw:p-(--spacing-16)',
    },
  ],
  defaultVariants: {
    version: 'v1',
    variant: 'surface',
  },
});

export interface PageProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof pageVariants> {
  /** Visual style version. Defaults to v1. Pass `version="v0"` for the legacy surface. */
  version?: PageVersion;
  /** Surface rendering. Defaults to `surface`; use `surfaceless` for a bare page. */
  variant?: PageVariant;
}

const Page = ({ className, variant = 'surface', version = 'v1', ...props }: PageProps) => {
  return <div className={cn(pageVariants({ version, variant }), className)} {...props}></div>;
};

export { Page };
